import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    CircularProgress,
} from '@mui/material';
import Modal from '../../general/modal';

export interface RoleOption {
    id: number;
    name: string;
    /**
     * SHER-598: may the REQUESTING researcher assign this role? Computed
     * server-side by the assignment ceiling. `undefined` (a pre-SHER-624 API
     * response) degrades to "show it" so nothing disappears unexpectedly.
     */
    assignableByMe?: boolean;
}

interface RoleAssignmentModalProps {
    open: boolean;
    /** Email of the user whose role is being changed (read-only display). */
    email: string;
    roles: RoleOption[];
    currentRoleId?: number | null;
    onSave: (roleId: number) => void;
    onClose: () => void;
    /** Save in flight — disables the form and shows a spinner. */
    loading?: boolean;
    /** Server denial (or other) reason to show inline; form stays open to retry. */
    error?: React.ReactNode;
    /** Maps a raw role name to its display label (e.g. i18n). Default: identity. */
    roleLabel?: (name: string) => string;
}

/**
 * SHER-624 — the investigation "Change user" role modal.
 *
 * Presentational only: the parent (`ShareInvestigation`) owns the API call and
 * drives `loading` / `error`. Two behaviours this modal exists to fix:
 *  - it lists ONLY roles the actor may assign (`assignableByMe !== false`), so a
 *    LAB_MANAGER never sees "Superadmin" in the dropdown; and
 *  - it renders the server's denial reason (`error`) instead of the previous
 *    silent `console.log`.
 *
 * NOTE (security): hiding a role here is UX only — the API ceiling (SHER-598)
 * remains the real boundary. Widening that ceiling is SHER-599, not this modal.
 */
export default function RoleAssignmentModal({
    open,
    email,
    roles,
    currentRoleId = null,
    onSave,
    onClose,
    loading = false,
    error = null,
    roleLabel = (n) => n,
}: RoleAssignmentModalProps) {
    const [selectedRoleId, setSelectedRoleId] = useState<number | ''>(
        currentRoleId != null ? currentRoleId : '',
    );

    const assignableRoles = roles.filter((r) => r.assignableByMe !== false);

    const canSave = selectedRoleId !== '' && !loading;
    const handleSave = () => {
        if (!canSave) return;
        onSave(Number(selectedRoleId));
    };

    return (
        <Modal
            open={open}
            closeModal={onClose}
            title={<Translate id="investigation.share.edit_researcher" />}
            confirmAction={handleSave}
            confirmButtonLabel="general.save"
            disabled={!canSave}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
                <TextField
                    label={<Translate id="investigation.share.form.email" />}
                    value={email}
                    fullWidth
                    disabled
                    InputProps={{ readOnly: true }}
                    inputProps={{ 'data-testid': 'role-assignment-email' } as any}
                />

                <TextField
                    select
                    label={<Translate id="investigation.share.form.role" />}
                    value={selectedRoleId === '' ? '' : String(selectedRoleId)}
                    onChange={(e) =>
                        setSelectedRoleId(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    fullWidth
                    disabled={loading}
                    SelectProps={{ 'data-testid': 'role-select' } as any}
                >
                    {assignableRoles.map((role) => (
                        <MenuItem key={role.id} value={String(role.id)}>
                            {roleLabel(role.name)}
                        </MenuItem>
                    ))}
                </TextField>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} data-testid="role-assignment-loading" />
                    </Box>
                )}

                {error && (
                    <Typography color="error" data-testid="role-assignment-error">
                        {error}
                    </Typography>
                )}
            </Box>
        </Modal>
    );
}
